import { addMonths, format } from 'date-fns'
import { useCallback, useEffect, useRef, useState } from 'react'
import type { Swiper as SwiperType } from 'swiper'
import { createMonthData, type MonthData } from '~entities/index'

import { getInitialDate, getLeftEdgeSlideIndex } from '~sharedLib'

interface UseMonthDataProps {
	onCurrentMonthChange?: (month: { index: number; id: string; date: Date }) => void
}

export const useMonthData = ({ onCurrentMonthChange }: UseMonthDataProps = {}) => {
	const swiperRef = useRef<SwiperType | null>(null)
	const lastMonthIdRef = useRef<string | null>(null)

	const [months, setMonths] = useState<MonthData[]>(() => {
		const initial = getInitialDate()
		return [
			createMonthData(initial),
			createMonthData(addMonths(initial, 1))
		]
	})

	const emitCurrentMonth = useCallback(() => {
		const swiper = swiperRef.current
		if (!swiper) return

		const index = getLeftEdgeSlideIndex(swiper)
		const month = months[index]
		if (!month) return

		if (lastMonthIdRef.current === month.id) return

		lastMonthIdRef.current = month.id

		onCurrentMonthChange?.({
			index,
			id: month.id,
			date: month.date
		})
	}, [months, onCurrentMonthChange])

	const handleMonthLoaded = useCallback((monthId: string) => {
		setMonths((prev) => prev.map((m) => m.id === monthId ? { ...m, isLoaded: true } : m))
	}, [])

	const tryAddNextMonth = useCallback(() => {
		const swiper = swiperRef.current
		if (!swiper || (swiper.progress < 0.99 && !swiper.isEnd)) return

		setMonths((prev) => {
			const lastMonth = prev.at(-1)
			if (!lastMonth?.isLoaded) return prev

			const nextDate = addMonths(lastMonth.date, 1)
			const nextId = format(nextDate, 'yyyy-MM')

			if (prev.some((m) => m.id === nextId)) return prev
			return [...prev, createMonthData(nextDate)]
		})
	}, [])

	const tryAddPrevMonth = useCallback(() => {
		const swiper = swiperRef.current
		if (!swiper) return

		setMonths((prev) => {
			const firstMonth = prev.at(0)
			if (!firstMonth?.isLoaded) return prev

			const prevDate = addMonths(firstMonth.date, -1)
			const prevId = format(prevDate, 'yyyy-MM')

			if (prev.some((m) => m.id === prevId)) return prev

			return [createMonthData(prevDate), ...prev]
		})

		requestAnimationFrame(() => {
			swiper.update()
			swiper.setTranslate(0)
			requestAnimationFrame(() => {
				emitCurrentMonth()
			})
		})
	}, [emitCurrentMonth])

	useEffect(() => {
		lastMonthIdRef.current = null
	}, [months.length])

	useEffect(() => {
		requestAnimationFrame(() => {
			emitCurrentMonth()
		})
	}, [months, emitCurrentMonth])
	return {
		swiperRef,
		months,
		emitCurrentMonth,
		handleMonthLoaded,
		tryAddNextMonth,
		tryAddPrevMonth
	}
}
